import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Package, Plus, Search, Filter, AlertTriangle, 
  MoreVertical, Edit, Trash2, Archive, Eye,
  ArrowUpDown, RefreshCw, Settings2, ChevronDown,
  PackagePlus, PackageMinus, Box, Layers
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import { useLanguage } from "../context/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const unitOptions = ["pcs", "packs", "boxes", "kg", "g", "liters", "ml", "units", "sets"];

export default function Inventory() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [items, setItems] = useState([]);
  const [classifications, setClassifications] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterClassification, setFilterClassification] = useState("");
  const [showLowStockOnly, setShowLowStockOnly] = useState(false);
  
  // Item dialog
  const [showItemDialog, setShowItemDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [itemForm, setItemForm] = useState({
    name: "",
    classification_id: "",
    unit: "pcs",
    sku: "",
    cost_price: "",
    selling_price: "",
    track_quantity: true,
    current_quantity: 0,
    reorder_level: ""
  });
  
  // Adjust stock dialog
  const [showAdjustDialog, setShowAdjustDialog] = useState(false);
  const [adjustingItem, setAdjustingItem] = useState(null);
  const [adjustForm, setAdjustForm] = useState({
    adjustment_type: "add",
    quantity: "",
    reason: ""
  });
  
  // Classification dialog
  const [showClassDialog, setShowClassDialog] = useState(false);
  const [classForm, setClassForm] = useState({ name: "", type: "Physical" });

  useEffect(() => {
    loadData();
  }, [filterClassification, showLowStockOnly]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [itemsRes, classRes, summaryRes] = await Promise.all([
        axios.get(`${API}/inventory/items`, {
          params: {
            classification_id: filterClassification || undefined,
            low_stock_only: showLowStockOnly || undefined
          }
        }),
        axios.get(`${API}/inventory/classifications`),
        axios.get(`${API}/inventory/reports/summary`)
      ]);
      setItems(itemsRes.data);
      setClassifications(classRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error("Failed to load inventory:", error);
      toast.error("Failed to load inventory");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveItem = async () => {
    if (!itemForm.name.trim()) {
      toast.error("Item name is required");
      return;
    }

    try {
      const payload = {
        ...itemForm,
        cost_price: itemForm.cost_price ? parseFloat(itemForm.cost_price) : null,
        selling_price: itemForm.selling_price ? parseFloat(itemForm.selling_price) : null,
        current_quantity: parseFloat(itemForm.current_quantity) || 0,
        reorder_level: itemForm.reorder_level ? parseFloat(itemForm.reorder_level) : null
      };

      if (editingItem) {
        await axios.put(`${API}/inventory/items/${editingItem.id}`, payload);
        toast.success("Item updated");
      } else {
        await axios.post(`${API}/inventory/items`, payload);
        toast.success("Item created");
      }
      
      setShowItemDialog(false);
      resetItemForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save item");
    }
  };

  const handleDeleteItem = async (item) => {
    if (!confirm(`Archive "${item.name}"?`)) return;
    
    try {
      await axios.delete(`${API}/inventory/items/${item.id}`);
      toast.success("Item archived");
      loadData();
    } catch (error) {
      toast.error("Failed to archive item");
    }
  };

  const handleAdjustStock = async () => {
    if (!adjustForm.quantity || !adjustForm.reason) {
      toast.error("Quantity and reason are required");
      return;
    }

    try {
      await axios.post(`${API}/inventory/adjust-stock`, {
        item_id: adjustingItem.id,
        adjustment_type: adjustForm.adjustment_type,
        quantity: parseFloat(adjustForm.quantity),
        reason: adjustForm.reason
      });
      toast.success("Stock adjusted");
      setShowAdjustDialog(false);
      setAdjustForm({ adjustment_type: "add", quantity: "", reason: "" });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to adjust stock");
    }
  };

  const handleSaveClassification = async () => {
    if (!classForm.name.trim()) {
      toast.error("Classification name is required");
      return;
    }

    try {
      await axios.post(`${API}/inventory/classifications`, classForm);
      toast.success("Classification created");
      setShowClassDialog(false);
      setClassForm({ name: "", type: "Physical" });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create classification");
    }
  };

  const resetItemForm = () => {
    setEditingItem(null);
    setItemForm({
      name: "",
      classification_id: "",
      unit: "pcs",
      sku: "",
      cost_price: "",
      selling_price: "",
      track_quantity: true,
      current_quantity: 0,
      reorder_level: ""
    });
  };

  const openEditItem = (item) => {
    setEditingItem(item);
    setItemForm({
      name: item.name,
      classification_id: item.classification_id || "",
      unit: item.unit || "pcs",
      sku: item.sku || "",
      cost_price: item.cost_price?.toString() || "",
      selling_price: item.selling_price?.toString() || "",
      track_quantity: item.track_quantity !== false,
      current_quantity: item.current_quantity || 0,
      reorder_level: item.reorder_level?.toString() || ""
    });
    setShowItemDialog(true);
  };

  const filteredItems = items.filter(item => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      item.name.toLowerCase().includes(query) ||
      (item.sku && item.sku.toLowerCase().includes(query))
    );
  });

  return (
    <div className="space-y-6" data-testid="inventory-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Package className="w-7 h-7 text-primary" />
            Inventory
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Track your stock and manage inventory items
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => navigate("/dashboard/inventory/classifications")}
            data-testid="manage-classifications-btn"
          >
            <Layers className="w-4 h-4 mr-2" />
            Classifications
          </Button>
          <Button onClick={() => { resetItemForm(); setShowItemDialog(true); }} data-testid="add-item-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Item
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Box className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{summary.total_items}</p>
                <p className="text-sm text-gray-500">Total Items</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Package className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">${summary.total_value.toLocaleString()}</p>
                <p className="text-sm text-gray-500">Stock Value</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{summary.low_stock_count}</p>
                <p className="text-sm text-gray-500">Low Stock</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <Package className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{summary.out_of_stock_count}</p>
                <p className="text-sm text-gray-500">Out of Stock</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search items..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-input"
          />
        </div>
        <Select value={filterClassification || "__all__"} onValueChange={(v) => setFilterClassification(v === "__all__" ? "" : v)}>
          <SelectTrigger className="w-[180px]" data-testid="filter-classification">
            <SelectValue placeholder="All Classifications" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Classifications</SelectItem>
            {classifications.map((c) => (
              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant={showLowStockOnly ? "default" : "outline"}
          onClick={() => setShowLowStockOnly(!showLowStockOnly)}
          data-testid="low-stock-filter"
        >
          <AlertTriangle className="w-4 h-4 mr-2" />
          Low Stock
        </Button>
        <Button variant="outline" onClick={loadData} data-testid="refresh-btn">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Items Table */}
      <div className="bg-white rounded-lg border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Item</TableHead>
              <TableHead>Classification</TableHead>
              <TableHead className="text-right">Stock</TableHead>
              <TableHead className="text-right">Cost</TableHead>
              <TableHead className="text-right">Price</TableHead>
              <TableHead className="text-center">Status</TableHead>
              <TableHead className="w-10"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                  Loading inventory...
                </TableCell>
              </TableRow>
            ) : filteredItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                  No items found. Add your first inventory item.
                </TableCell>
              </TableRow>
            ) : (
              filteredItems.map((item) => (
                <TableRow key={item.id} className="hover:bg-gray-50" data-testid={`item-row-${item.id}`}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{item.name}</p>
                      {item.sku && <p className="text-xs text-gray-500">SKU: {item.sku}</p>}
                    </div>
                  </TableCell>
                  <TableCell>
                    {item.classification ? (
                      <Badge variant="outline">{item.classification.name}</Badge>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {item.track_quantity ? (
                      <span className="font-mono">
                        {item.current_quantity} {item.unit}
                      </span>
                    ) : (
                      <span className="text-gray-400">N/A</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {item.cost_price ? `$${item.cost_price.toFixed(2)}` : "-"}
                  </TableCell>
                  <TableCell className="text-right">
                    {item.selling_price ? `$${item.selling_price.toFixed(2)}` : "-"}
                  </TableCell>
                  <TableCell className="text-center">
                    {!item.track_quantity ? (
                      <Badge variant="secondary">Service</Badge>
                    ) : item.current_quantity <= 0 ? (
                      <Badge variant="destructive">Out of Stock</Badge>
                    ) : item.is_low_stock ? (
                      <Badge variant="warning" className="bg-amber-100 text-amber-800">Low Stock</Badge>
                    ) : (
                      <Badge variant="success" className="bg-emerald-100 text-emerald-800">In Stock</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" data-testid={`item-menu-${item.id}`}>
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/dashboard/inventory/items/${item.id}`)}>
                          <Eye className="w-4 h-4 mr-2" />
                          View Details
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openEditItem(item)}>
                          <Edit className="w-4 h-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        {item.track_quantity && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => { setAdjustingItem(item); setShowAdjustDialog(true); }}>
                              <PackagePlus className="w-4 h-4 mr-2" />
                              Adjust Stock
                            </DropdownMenuItem>
                          </>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => handleDeleteItem(item)} className="text-red-600">
                          <Archive className="w-4 h-4 mr-2" />
                          Archive
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Add/Edit Item Dialog */}
      <Dialog open={showItemDialog} onOpenChange={setShowItemDialog}>
        <DialogContent className="max-w-lg" data-testid="item-dialog">
          <DialogHeader>
            <DialogTitle>{editingItem ? "Edit Item" : "Add Inventory Item"}</DialogTitle>
            <DialogDescription>
              {editingItem ? "Update item details" : "Add a new item to your inventory"}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Item Name *</Label>
              <Input
                value={itemForm.name}
                onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
                placeholder="e.g., iPhone 15 Pro Case"
                data-testid="item-name-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Classification</Label>
                <Select
                  value={itemForm.classification_id || "__none__"}
                  onValueChange={(v) => setItemForm({ ...itemForm, classification_id: v === "__none__" ? "" : v })}
                >
                  <SelectTrigger data-testid="item-classification-select">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    {classifications.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                    <div className="border-t mt-1 pt-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full justify-start"
                        onClick={() => { setShowItemDialog(false); setShowClassDialog(true); }}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Classification
                      </Button>
                    </div>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Unit</Label>
                <Select
                  value={itemForm.unit}
                  onValueChange={(v) => setItemForm({ ...itemForm, unit: v })}
                >
                  <SelectTrigger data-testid="item-unit-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {unitOptions.map((u) => (
                      <SelectItem key={u} value={u}>{u}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>SKU (Optional)</Label>
              <Input
                value={itemForm.sku}
                onChange={(e) => setItemForm({ ...itemForm, sku: e.target.value })}
                placeholder="e.g., SKU-001"
                data-testid="item-sku-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Cost Price</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={itemForm.cost_price}
                  onChange={(e) => setItemForm({ ...itemForm, cost_price: e.target.value })}
                  placeholder="0.00"
                  data-testid="item-cost-input"
                />
              </div>
              <div>
                <Label>Selling Price</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={itemForm.selling_price}
                  onChange={(e) => setItemForm({ ...itemForm, selling_price: e.target.value })}
                  placeholder="0.00"
                  data-testid="item-price-input"
                />
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="track_quantity"
                checked={itemForm.track_quantity}
                onChange={(e) => setItemForm({ ...itemForm, track_quantity: e.target.checked })}
                className="rounded border-gray-300"
                data-testid="item-track-qty-checkbox"
              />
              <Label htmlFor="track_quantity" className="font-normal">
                Track quantity for this item
              </Label>
            </div>
            
            {itemForm.track_quantity && (
              <div className="grid grid-cols-2 gap-4">
                {!editingItem && (
                  <div>
                    <Label>Initial Stock</Label>
                    <Input
                      type="number"
                      value={itemForm.current_quantity}
                      onChange={(e) => setItemForm({ ...itemForm, current_quantity: parseFloat(e.target.value) || 0 })}
                      data-testid="item-stock-input"
                    />
                  </div>
                )}
                <div>
                  <Label>Reorder Level</Label>
                  <Input
                    type="number"
                    value={itemForm.reorder_level}
                    onChange={(e) => setItemForm({ ...itemForm, reorder_level: e.target.value })}
                    placeholder="Alert when stock is low"
                    data-testid="item-reorder-input"
                  />
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowItemDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveItem} data-testid="save-item-btn">
              {editingItem ? "Update" : "Add Item"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Adjust Stock Dialog */}
      <Dialog open={showAdjustDialog} onOpenChange={setShowAdjustDialog}>
        <DialogContent data-testid="adjust-stock-dialog">
          <DialogHeader>
            <DialogTitle>Adjust Stock</DialogTitle>
            <DialogDescription>
              {adjustingItem && (
                <span>Adjust stock for <strong>{adjustingItem.name}</strong> (Current: {adjustingItem.current_quantity} {adjustingItem.unit})</span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Adjustment Type</Label>
              <Select
                value={adjustForm.adjustment_type}
                onValueChange={(v) => setAdjustForm({ ...adjustForm, adjustment_type: v })}
              >
                <SelectTrigger data-testid="adjust-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="add">
                    <span className="flex items-center gap-2">
                      <PackagePlus className="w-4 h-4 text-emerald-600" />
                      Add Stock
                    </span>
                  </SelectItem>
                  <SelectItem value="remove">
                    <span className="flex items-center gap-2">
                      <PackageMinus className="w-4 h-4 text-red-600" />
                      Remove Stock
                    </span>
                  </SelectItem>
                  <SelectItem value="set">
                    <span className="flex items-center gap-2">
                      <RefreshCw className="w-4 h-4 text-blue-600" />
                      Set Exact Count
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Quantity</Label>
              <Input
                type="number"
                value={adjustForm.quantity}
                onChange={(e) => setAdjustForm({ ...adjustForm, quantity: e.target.value })}
                placeholder="Enter quantity"
                data-testid="adjust-qty-input"
              />
            </div>
            
            <div>
              <Label>Reason *</Label>
              <Textarea
                value={adjustForm.reason}
                onChange={(e) => setAdjustForm({ ...adjustForm, reason: e.target.value })}
                placeholder="Why are you adjusting stock? (Required)"
                data-testid="adjust-reason-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdjustDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAdjustStock} data-testid="confirm-adjust-btn">
              Adjust Stock
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Classification Dialog */}
      <Dialog open={showClassDialog} onOpenChange={setShowClassDialog}>
        <DialogContent data-testid="classification-dialog">
          <DialogHeader>
            <DialogTitle>Add Classification</DialogTitle>
            <DialogDescription>
              Create a new category for your inventory items
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={classForm.name}
                onChange={(e) => setClassForm({ ...classForm, name: e.target.value })}
                placeholder="e.g., Electronics, Stationery"
                data-testid="class-name-input"
              />
            </div>
            <div>
              <Label>Type</Label>
              <Select
                value={classForm.type}
                onValueChange={(v) => setClassForm({ ...classForm, type: v })}
              >
                <SelectTrigger data-testid="class-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Physical">Physical Goods</SelectItem>
                  <SelectItem value="Digital">Digital Products</SelectItem>
                  <SelectItem value="Float/Balance">Float / Balance</SelectItem>
                  <SelectItem value="Service">Service</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowClassDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveClassification} data-testid="save-class-btn">
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
